import { scoreMap, complexityScoreMap } from './constants';

/**
 * Calculate weighted score for a process
 * Formula: Σ(Impact × Importance) across all 4 categories
 * Source: proc_list_viewer.html:282-315
 */
export function calculateScore(process) {
  if (!process) return '0.00';

  const prodImpact = scoreMap[process.impact_productivity] || 0;
  const custImpact = scoreMap[process.impact_customer] || 0;
  const agentImpact = scoreMap[process.impact_agent] || 0;
  const complianceImpact = scoreMap[process.impact_compliance] || 0;

  const prodImportance = scoreMap[process.importance_productivity] || 0;
  const custImportance = scoreMap[process.importance_customer] || 0;
  const agentImportance = scoreMap[process.importance_agent] || 0;
  const complianceImportance = scoreMap[process.importance_compliance] || 0;

  const score =
    (prodImpact * prodImportance) +
    (custImpact * custImportance) +
    (agentImpact * agentImportance) +
    (complianceImpact * complianceImportance);

  return score.toFixed(2);
}

/**
 * Calculate weighted impact level based on score
 * Source: proc_list_viewer.html:318-325
 */
export function getWeightedImpact(score) {
  const numScore = parseFloat(score);
  if (numScore === 0) return 'None';
  if (numScore > 0 && numScore < 1) return 'Low';
  if (numScore >= 1 && numScore < 2) return 'Medium-Low';
  if (numScore >= 2 && numScore < 3) return 'Medium-High';
  if (numScore >= 3) return 'High';
  return 'None';
}

/**
 * Get complexity score from complexity level
 */
export function getComplexityScore(complexityLevel) {
  return complexityScoreMap[complexityLevel] || 0;
}

/**
 * Determine category/quadrant based on impact and complexity
 * Quadrants: Quick Win, Strategic, Fill-In, Hard Slog
 */
export function getCategory(weightedScore, complexityScore) {
  const score = parseFloat(weightedScore);
  const complexity = parseFloat(complexityScore);

  // High impact (score >= 2), Low complexity (<= 2) = Quick Win
  if (score >= 2 && complexity <= 2) return 'Quick Win';

  // High impact (score >= 2), High complexity (> 2) = Strategic
  if (score >= 2 && complexity > 2) return 'Strategic';

  // Low impact (score < 2), Low complexity (<= 2) = Fill-In
  if (score < 2 && complexity <= 2) return 'Fill-In';

  // Low impact (score < 2), High complexity (> 2) = Hard Slog
  if (score < 2 && complexity > 2) return 'Hard Slog';

  return 'Fill-In'; // Default
}

/**
 * Enrich process with calculated fields
 */
export function enrichProcess(process) {
  const weightedScore = calculateScore(process);
  const weightedImpact = getWeightedImpact(weightedScore);
  const complexityScore = getComplexityScore(process.complexity);
  const category = getCategory(weightedScore, complexityScore);

  return {
    ...process,
    weighted_score: weightedScore,
    weighted_impact: weightedImpact,
    complexity_score: complexityScore.toString(),
    category: category
  };
}

/**
 * Sort processes by weighted score (descending) and renumber as PROC1, PROC2, etc.
 * Source: proc_list_viewer.html:400-427
 */
export function sortAndRenumberProcesses(processes) {
  // Enrich all processes with calculated fields
  const enrichedProcesses = processes.map(enrichProcess);

  // Sort by weighted score (descending)
  enrichedProcesses.sort((a, b) => {
    const scoreA = parseFloat(a.weighted_score);
    const scoreB = parseFloat(b.weighted_score);
    return scoreB - scoreA;
  });

  // Renumber as PROC1, PROC2, etc.
  return enrichedProcesses.map((process, index) => ({
    ...process,
    id: `PROC${index + 1}`
  }));
}
